import { getBrandAndBrandOwner, isFocusBrandOwnerGroup } from '../../common/brandOwner.utils';
import { Formatting } from '../../data/formatting';
import { DataStore } from '../../data/store';
import { DataColumn, DataRow, DataType, FormatHint, KpiWithChangeColumn } from '../../types';
import { getOutOfStackRows } from './billboardUtils';
import { BoardInfo, BoardRow, ColumnConfig, RowBase, Sorting, ValueColor } from './types';

type ColumnInfo = {
  name: string;
  label: string;
  isChange: boolean;
  formatHint?: FormatHint;
  currency: string;
  dataType: DataType;
  arrayLength: number | null;
};

export function makeBillboardInfo(
  data: DataStore,
  options: {
    title?: string;
    grouping: string;
    highlight: string;
    highlightColumn?: string;
    siblingBuffer: number;
    limit: number;
    columnConfigs: ColumnConfig[];
    sorting?: Sorting;
    highlightedBrands?: string[];
    billboardDataType: string;
  }
): BoardInfo {
  const { title, highlightColumn, limit, sorting, columnConfigs, highlightedBrands, billboardDataType } = options;
  const focusTypeBrandOwnerGroup = isFocusBrandOwnerGroup(options.highlight);
  const { brand: brandName, brandOwner: brandOwnerName = '' } = getBrandAndBrandOwner(options.highlight);
  const highlight = focusTypeBrandOwnerGroup ? brandOwnerName : brandName;
  const grouping = options.grouping;
  const groupedData = [grouping, billboardDataType]
    .map(key => data.getGroupedData([key])[0])
    .find(group => group?.data?.length > 0) ?? { data: [] };

  const columns = data.getDataColumns();
  const currency = data.getCurrency();
  const board: BoardInfo = {
    title,
    rows: [],
    columns: [],
  };

  if (groupedData.data.length === 0) {
    return board;
  }
  const columnInfos = columnConfigs.map(config => getColumnInfo(columns, currency, config));

  const baseRows: RowBase[] = groupedData?.data?.map(row => {
    const identifiers = DataStore.getRowIdentifiers(row, columns, grouping);
    const highlightValue = highlightColumn ? String(row.values[highlightColumn]) : identifiers.id;
    return {
      id: identifiers.id,
      name: identifiers.name,
      highlight: highlightValue,
      sortValue: sorting ? DataStore.getCellsLatestValue(row, sorting.by) : undefined,
      values: columnInfos.map(inf => getValue(row, inf)),
      brandOwner: row.values?.brand_owner_name,
      rank: -1,
    };
  });

  if (sorting) {
    baseRows.sort((a, b) => (sorting.direction === 'asc' ? a.sortValue - b.sortValue : b.sortValue - a.sortValue));
  }

  baseRows.forEach((row, idx) => {
    row.rank = idx + 1;
  });

  const limitedRows = baseRows.slice(0, limit);

  const outOfStackRows: RowBase[] = getOutOfStackRows(
    baseRows,
    limitedRows,
    highlight,
    brandOwnerName,
    focusTypeBrandOwnerGroup,
    billboardDataType
  );

  // Filter out any out-of-stack rows that have the same brand owner as brands already in limited rows
  // This prevents the same brand from appearing in both sections
  const filteredOutOfStackRows = outOfStackRows.filter(
    outItem => !limitedRows.some(limitedItem => limitedItem.id === outItem.id)
  );
  let haveHighlighted = false;
  board.rows = [];
  limitedRows.forEach(row => {
    const shouldHighlight =
      highlightedBrands?.includes(row.highlight) ||
      (!haveHighlighted && row.highlight === highlight) ||
      row.brandOwner === highlight ||
      (billboardDataType !== 'brand_name' && brandOwnerName === row.brandOwner);
    board.rows.push(makeBoardRow(row, shouldHighlight, columnInfos));
    if (shouldHighlight) {
      haveHighlighted = true;
    }
  });

  if (filteredOutOfStackRows.length > 0) {
    board.outOfStackRows = [];

    // Sort out-of-stack rows by rank to get only the top-ranked ones
    const sortedOutOfStackRows = [...filteredOutOfStackRows].sort((a, b) => a.rank - b.rank);

    const topRankedOutOfStackRows = sortedOutOfStackRows.slice(0, 1);

    topRankedOutOfStackRows.forEach(row => {
      const shouldHighlight =
        highlightedBrands?.includes(row.highlight) ||
        (!haveHighlighted && row.highlight === highlight) ||
        row.brandOwner === highlight ||
        (billboardDataType !== 'brand_name' && brandOwnerName === row.brandOwner);
      board.outOfStackRows.push(makeBoardRow(row, shouldHighlight, columnInfos));
      if (shouldHighlight) {
        haveHighlighted = true;
      }
    });
  }

  board.columns = columnInfos.map(inf => inf.label);
  return board;
}

function getValue(row: DataRow, columnInfo: ColumnInfo): number | string | null {
  const value = row.values[columnInfo.name];
  if (Array.isArray(value)) {
    return value[value.length - 1];
  }

  return value;
}

function makeBoardRow(row: RowBase, isHighlighted: boolean, columnInfos: ColumnInfo[]): BoardRow {
  return {
    label: row.name,
    id: row.id,
    rank: row.rank,
    isHighlighted: isHighlighted,
    columns: columnInfos.map((inf, idx) => ({
      value: Formatting.format(row.values[idx], {
        dataType: inf.dataType,
        formatHint: inf.formatHint,
        isChange: inf.isChange,
        hyphenForNull: true,
        currency: inf.currency,
      }),
      color: getValueColor(row.values[idx], inf.isChange),
      name: '',
    })),
  };
}

function getValueColor(value: number | string | null, isChange: boolean): ValueColor {
  if (typeof value === 'string' || value === null) {
    return 'text-inactive';
  }
  if (!isChange) {
    return 'text-default';
  }
  return value < 0 ? 'text-ui-red' : 'text-ui-green';
}

function getColumnInfo(columns: DataColumn[], currency: string, config?: ColumnConfig): ColumnInfo | undefined {
  if (!config) {
    return;
  }

  const column = columns.find(col => col.name === config.name);
  if (column) {
    return {
      name: column.name,
      label: config.label || column.label,
      dataType: column.dataType,
      formatHint: column.formatHint,
      isChange: false,
      arrayLength: column.arrayLength,
      currency,
    };
  }

  const changeColumn = columns.find(
    col => (col as KpiWithChangeColumn)?.changeName === config.name
  ) as KpiWithChangeColumn;
  if (changeColumn) {
    return {
      name: changeColumn.changeName,
      label: config.label || changeColumn.changeName,
      dataType: changeColumn.changeDataType,
      formatHint: changeColumn.changeFormatHint,
      isChange: true,
      arrayLength: changeColumn.arrayLength,
      currency,
    };
  }
}
